import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Link,
  IconButton,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Container,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  LockOutlined
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import UpdatePasswordModal from './UpdatePasswordModal';

const Login: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Tela de acesso interno não deve aparecer no Google.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authService.login({ email, password });
      navigate('/');
    } catch (err: any) {

      console.log(err.response)
     if (err.response?.data.nova_senha == 1) {
        setUserId(err.response.data.user.id); // Pega o ID do usuário para atualizar a senha
        setIsModalOpen(true);
      } else if (err.response?.status === 400)  {
        setError(err.response.data.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xl" sx={{
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      py: 2,
      px: isMobile ? 2 : 4
    }}>
      <Card
        elevation={6}
        sx={{
          width: '100%',
          maxWidth: 1200,
          borderRadius: 2,
          overflow: 'hidden',
          background: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.5)'
            : '0 8px 32px rgba(145, 158, 171, 0.24)'
        }}
      >
        <Grid container sx={{ height: isMobile ? 'auto' : '600px' }}>
          {/* Lado esquerdo - Imagem/Banner (apenas visível em telas maiores) */}
          {!isMobile && (
            <Grid
              item
              xs={false}
              sm={5}
              md={6}
              sx={{
                backgroundImage: 'url(https://source.unsplash.com/featured/?cars,dealership)',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                }
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  p: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  zIndex: 1
                }}
              >
                <img
                  src={`${process.env.PUBLIC_URL || ''}/assets/logos/LOGO - R MOTORS vertical 001.png`}
                  alt="R Motors"
                  style={{ maxHeight: 180, width: 'auto', objectFit: 'contain' }}
                  onError={(e) => {
                    const el = e.target as HTMLImageElement;
                    el.style.display = 'none';
                    const next = el.nextElementSibling as HTMLElement;
                    if (next) next.style.display = 'block';
                  }}
                />
                <Typography component="span" variant="h3" fontWeight="bold" gutterBottom sx={{ color: '#F00000', display: 'none' }}>
                  R Motors
                </Typography>
                <Typography variant="h6" sx={{ mt: 2, color: 'white' }}>
                  Gerencie seu negócio de veículos com facilidade
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Lado direito - Formulário de Login */}
          <Grid
            item
            xs={12}
            sm={7}
            md={6}
            component={Paper}
            elevation={0}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              background: 'transparent'
            }}
          >
            <CardContent sx={{
              p: isMobile ? 3 : 4,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              justifyContent: 'center'
            }}>
              {/* Logo em telas móveis */}
              {isMobile && (
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
                  <img
                    src={`${process.env.PUBLIC_URL || ''}/assets/logos/LOGO - R MOTORS vertical 001.png`}
                    alt="R Motors"
                    style={{ maxHeight: 80, width: 'auto', objectFit: 'contain' }}
                    onError={(e) => {
                      const el = e.target as HTMLImageElement;
                      el.style.display = 'none';
                      const next = el.nextElementSibling as HTMLElement;
                      if (next) next.style.display = 'block';
                    }}
                  />
                  <Typography component="span" variant="h4" fontWeight="bold" color="primary" gutterBottom sx={{ display: 'none' }}>
                    R Motors
                  </Typography>
                </Box>
              )}

              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 3
              }}>
                <LockOutlined
                  color="primary"
                  sx={{ fontSize: 32, mr: 2 }}
                />
                <Typography component="h1" variant={isMobile ? "h5" : "h4"} fontWeight="500">
                  Login
                </Typography>
              </Box>

              {error && (
                <Typography
                  color="error"
                  variant="body2"
                  sx={{ mb: 2 }}
                >
                  {error}
                </Typography>
              )}

              {!error && (
                <Typography
                  color="text.secondary"
                  variant="body2"
                  sx={{ mb: 2 }}
                >
                  Seja bem-vindo!
                </Typography>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      bgcolor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.03)',
                    }
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Senha"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      bgcolor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.03)',
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mb: 3
                }}>
                  <Link component={RouterLink} to="/recuperar-senha" variant="body2">
                    Esqueceu sua senha?
                  </Link>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disableElevation
                  size="large"
                  disabled={loading}
                  startIcon={<LoginIcon />}
                  sx={{
                    mt: 1,
                    mb: 3,
                    height: 48,
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                >
                  {loading ? 'Entrando...' : 'Entrar'}
                </Button>

                <Divider sx={{ my: 3 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ px: 1 }}
                  >
                    OU
                  </Typography>
                </Divider>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Ainda não tem uma conta?
                  </Typography>
                  {/* <Button
                    component={RouterLink}
                    to="/cadastro"
                    variant="outlined"
                    fullWidth
                    size="large"
                    sx={{ 
                      height: 48,
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 500
                    }}
                  >
                    Criar conta
                  </Button> */}
                </Box>
              </Box>
            </CardContent>
          </Grid>
        </Grid>
      </Card>
      {userId && <UpdatePasswordModal open={isModalOpen} onClose={() => setIsModalOpen(false)} userId={userId} />}

    </Container>
  );
};

export default Login; 